import SpotifyIcon from '../assets/images/admin/Spotify.svg';
import CustomlinkIcon from '../assets/images/icons/customLink.png';
import MerchTshirtIcon from '../assets/images/icons/merch-tshirt.png';
import SellTicketIcon from '../assets/images/icons/sell-ticket.png';
import BioLinkIcon from '../assets/images/admin/bio-link.png';
import YoutubeIcon from '../assets/images/admin/Youtube.png';
import DefaultCampaignImage from '../assets/images/admin/social-img2.png';

const TARGET_NAME_ICONS = {
    'Link Promotion': CustomlinkIcon,
    'Sell Tickets': SellTicketIcon,
    'Sell Merch': MerchTshirtIcon,
    'Bio Link Promotion': BioLinkIcon,
};

const OBJECTIVE_LIST_LABELS = {
    'YouTube Growth': 'YouTube Growth Campaign',
    'Promote Bio Link': 'Bio Link Campaign',
    'Promote Custom Link': 'Custom Link Campaign',
    'Promote an Event': 'Event Campaign',
    'Sell Your Merch': 'Merch Campaign',
};

const CAMPAIGN_GOAL_DESCRIPTIONS = {
    'More Streams': 'Your ads will optimize to drive more streams to your music',
    'Spotify Growth': 'Your ads will optimize to grow your Spotify followers',
    'Instagram Growth': 'Your ads will optimize to grow your Instagram followers',
    'Playlist Growth': 'Your ads will optimize to grow playlist engagement',
    'YouTube Growth': 'Your ads will optimize to drive YouTube views and engagement',
    'Promote Custom Link': 'Your ads will optimize to drive traffic to your custom link',
    'Promote an Event': 'Your ads will optimize to sell tickets to your event',
    'Sell Your Merch': 'Your ads will optimize to increase merch sales',
    'Promote Bio Link': 'Your ads will optimize to turn visibility into real fans',
};

const TARGET_NAME_LIST_LABELS = {
    'Bio Link Promotion': 'Bio Link Campaign',
    'Link Promotion': 'Custom Link Campaign',
    'Sell Tickets': 'Event Campaign',
    'Sell Merch': 'Merch Campaign',
};

/**
 * Campaign objective name for list / history / details UI buttons.
 * Prefers the matched goal option name (e.g. "Sell Your Merch").
 */
export const getCampaignObjectiveLabel = (campaign, goalMatch) => {
    if (goalMatch?.name) {
        return goalMatch.name;
    }

    const campaignName = campaign?.name;
    if (campaignName) {
        const matchedKey = Object.keys(OBJECTIVE_LIST_LABELS).find((key) =>
            campaignName.toLowerCase().includes(key.toLowerCase())
        );
        if (matchedKey) return matchedKey;
    }

    const targetName = campaign?.targetObject?.targetName;
    if (targetName && TARGET_NAME_LIST_LABELS[targetName]) {
        // Map target type back to the objective name when goalMatch is missing
        const targetToObjective = {
            'Bio Link Promotion': 'Promote Bio Link',
            'Link Promotion': 'Promote Custom Link',
            'Sell Tickets': 'Promote an Event',
            'Sell Merch': 'Sell Your Merch',
        };
        return targetToObjective[targetName] || TARGET_NAME_LIST_LABELS[targetName];
    }

    return campaignName || 'Campaign';
};

/**
 * Performance-section goal copy for campaign editor / details.
 * Uses the matched objective when available, with a fallback from goalOptions.desc.
 */
export const getCampaignGoalDescription = (goalMatch, goalOptions = []) => {
    const name = goalMatch?.name;
    if (name && CAMPAIGN_GOAL_DESCRIPTIONS[name]) {
        return CAMPAIGN_GOAL_DESCRIPTIONS[name];
    }

    const goal = goalOptions.find((item) => item.value === name);
    if (goal?.desc) {
        const desc = goal.desc.trim();
        const normalized = desc.charAt(0).toLowerCase() + desc.slice(1);
        return `Your ads will optimize to ${normalized}`;
    }

    return 'Your ads will optimize to reach your campaign goal';
};

const isBootstrapOrEventIcon = (icon) =>
    typeof icon === 'string' && (icon.startsWith('bi') || icon.startsWith('event'));

const asPhoto = (src) => ({ src, isIcon: false });
const asIcon = (src) => ({ src, isIcon: true });

/**
 * Returns the best thumbnail for a campaign card.
 * Priority: real target image → enriched link image → static target-type icon → objective icon → default.
 */
export const getCampaignThumbnail = (campaign, goalMatch) => {
    const target = campaign?.targetObject;
    if (!target) {
        const fallback = getObjectiveImageFallback(goalMatch);
        return fallback ? asIcon(fallback) : asIcon(DefaultCampaignImage);
    }

    const { targetType, targetName, songImage, profilePicture, targetImage } = target;

    if (targetType === 'instaProfile') {
        const profileUrl =
            campaign?.instagram_profile?.profile_picture_url ||
            campaign?.instagramInfo?.profile_picture_url ||
            profilePicture;
        if (profileUrl) return asPhoto(profileUrl);
    }

    if (targetType === 'song' && songImage) {
        return asPhoto(songImage);
    }

    if (targetType === 'spotifyProfile') {
        return asIcon(SpotifyIcon);
    }

    if (targetType === 'youtubeContent') {
        if (targetImage) return asPhoto(targetImage);
        return asIcon(YoutubeIcon);
    }

    if (targetType === 'others') {
        if (targetImage) return asPhoto(targetImage);
        if (campaign?.linkImage) return asPhoto(campaign.linkImage);
        if (targetName && TARGET_NAME_ICONS[targetName]) {
            return asIcon(TARGET_NAME_ICONS[targetName]);
        }
    }

    const fallback = getObjectiveImageFallback(goalMatch);
    return fallback ? asIcon(fallback) : asIcon(DefaultCampaignImage);
};

/** Image URL from objective config (skips bootstrap/event icons — those need a component). */
export const getObjectiveImageFallback = (goalMatch) => {
    if (!goalMatch?.icon || isBootstrapOrEventIcon(goalMatch.icon)) {
        return null;
    }
    return goalMatch.icon;
};

export const isBootstrapOrEventGoalIcon = isBootstrapOrEventIcon;

export const getCampaignTargetLabel = (campaign) => {
    const target = campaign?.targetObject;
    if (!target) return 'Overview';
    const campaignName = campaign?.name || '';

    if (target.targetType === 'instaProfile') {
        const username =
            campaign?.instagram_profile?.username ||
            campaign?.instagramInfo?.username ||
            target.instaUserName;
        return username ? `@${username}` : 'Instagram Profile';
    }

    if (target.targetType === 'song') {
        return target.trackName || 'Song';
    }

    if (target.targetType === 'spotifyProfile') {
        return 'Artist Profile';
    }

    if (target.targetType === 'youtubeContent') {
        return target.targetTitle
            || (target.contentType === 'channel' ? 'YouTube Channel' : 'YouTube Video');
    }

    if (target.targetType === 'others') {
        const objectiveLabelOverrides = {
            'YouTube Growth': 'YouTube Growth Campaign',
            'Promote Bio Link': 'Bio Link Campaign',
            'Promote Custom Link': 'Custom Link Campaign',
            'Sell Your Merch': 'Merch Campaign',
        };
        const matchedObjective = Object.keys(objectiveLabelOverrides).find((key) =>
            campaignName.toLowerCase().includes(key.toLowerCase())
        );
        if (matchedObjective) {
            return objectiveLabelOverrides[matchedObjective];
        }
        return target.targetTitle || campaign?.linkTitle || getCampaignObjectiveLabel(campaign);
    }

    return 'Overview';
};

export const getCampaignDisplayTitle = (campaign, artistInfo) => {
    const target = campaign?.targetObject;
    if (!target) return artistInfo?.name || '';

    if (target.targetType === 'youtubeContent') {
        const title = target.targetTitle || getCampaignTargetLabel(campaign);
        const channel = target.channelTitle || target.artistName || '';
        if (target.contentType === 'channel' || !channel || title === channel) {
            return title;
        }
        return `${title} - ${channel}`;
    }

    const artistName = target.artistName || artistInfo?.name || '';
    const label = getCampaignTargetLabel(campaign);

    if (!artistName) return label;
    if (label === 'Overview') return artistName;
    return `${label} - ${artistName}`;
};
